import {
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { UploadFileInfo } from '@progress/kendo-react-upload';
import {
  Contract,
  ContractBuildType,
  ContractEntitlement,
  IssueType,
  ServiceCategory,
  ServiceNowCreateBillingRequest,
  ServiceNowCreateCaseRequest,
  ServiceNowCreateIncidentRequest,
  ServiceNowCreateRequest,
  SnowCaseImpact,
  SnowRequestImpact,
} from 'cmp-client-api';
import { capitalise, createControlIdsFromSampleObject } from 'swo-ciam-common-react';
import { paramCase } from 'param-case';

import { useInjectableServices } from '@src/server/service-registry.provider';
import { createDeferredPromise, translateText } from '@src/domain/common';
import { fileToBase64 } from '@src/domain/common/utils/files/screenshot-utils';
import { LocaleTranslationKeysEnum } from '@src/core/i18n/locale-translation-keys.enum';
import {
  CaseCreateModelInterface,
  CaseCreateModelProps,
} from '@src/domain/pages/support/cases/cases-create/model/case-create-model.interface';
import {
  AllControlFieldIdsType,
  BillingCreateFormValues,
  CaseCreateFormValues,
  ControlFieldIdsCSM,
  ControlFieldIdsITSM,
  TicketCreateFormValues,
} from '@src/domain/pages/support/cases/cases-create/types/cases.type';
import { CSMType } from '@src/server';
import { IncidentImpactEnum } from '@src/domain/pages/support/tickets/types/incident-impact.enum';
import { RequestImpactEnum } from '@src/domain/pages/support/tickets/types/request-impact.enum';
import { RoutePathsEnum, RoutePathSuffixEnum } from '@src/core/routing/route-paths.enum';
import { SupportPrefetchContext } from '@src/domain/pages/support/common/context/support-prefetch-context-provider.component';
import { useFlag } from '@unleash/proxy-client-react';
import Flags from '@src/core/flags/types/flags.enum';
import { CaseImpactOrder } from '../../../tickets/commons/impact.order';

type SubCategory = {
  incidentCategories: {
    [key: string]: ServiceCategory;
  };
  srCategories: {
    [key: string]: ServiceCategory;
  };
  billingCategories: {
    [key: string]: ServiceCategory;
  };
  serviceRequest: {
    [key: string]: ServiceCategory;
  };
};

export const subcategoriesFields = createControlIdsFromSampleObject<SubCategory>({
  incidentCategories: undefined,
  srCategories: undefined,
  billingCategories: undefined,
  serviceRequest: undefined,
});

export const controlIdsFields = createControlIdsFromSampleObject<AllControlFieldIdsType>({
  title: undefined,
  type: undefined,
  contract: undefined,
  entitlement: undefined,
  filesAttached: undefined,
  serviceName: undefined,
  priority: undefined,
  impact: undefined,
  category: undefined,
  subCategory: undefined,
  subCategoryTask: undefined,
  cmdbConfigItem: undefined,
  shortDescription: undefined,
  description: undefined,
  descriptionHTML: undefined,
});

export const useCaseCreateModel = (props: CaseCreateModelProps): CaseCreateModelInterface => {
  const { scope } = props;
  const { SupportSystemService } = useInjectableServices();
  const navigate = useNavigate();

  const { serviceOptions } = useContext(SupportPrefetchContext);

  const [selectedService, setSelectedService] = useState<Contract>();
  const [selectedContract, setSelectedContract] = useState<{ contract: string; id: string }>();
  const areEntitlementsEnabled = useFlag(Flags.CaseEntitlements);

  const deferredContractsPromiseRef = useRef<ReturnType<typeof createDeferredPromise<void>> | null>(
    null
  );

  const deferredPromiseRef = useRef<ReturnType<typeof createDeferredPromise<void>> | null>(null);
  const areCasesConfigItemsEnabled = useFlag(Flags.CaseConfigItems);

  const getConfigurationItems = useCallback(async () => {
    if (selectedService && areCasesConfigItemsEnabled) {
      return SupportSystemService.getCaseConfigurationItems();
    }

    return [];
  }, [SupportSystemService, selectedService, areCasesConfigItemsEnabled]);

  const { data: configurationItems = [], refetch: refetchConfigurationItems } = useQuery(
    ['fetch-csm-config-items', getConfigurationItems],
    async () => getConfigurationItems(),
    { enabled: false }
  );

  const batchCategoriesRequest = useCallback(
    (selectedServiceContract: Contract, serviceType: ContractBuildType) => {
      const categoriesForITSM = [IssueType.Request, IssueType.Incident];
      const categoriesForCSM = [CSMType.Case];

      if (serviceType === ContractBuildType.ITSM) {
        return categoriesForITSM.flatMap((type: never) =>
          SupportSystemService.getServiceCategories(
            selectedServiceContract[controlIdsFields.serviceName],
            type
          )
        );
      }

      if (serviceType === ContractBuildType.CSM) {
        return categoriesForCSM.flatMap((type: never) =>
          SupportSystemService.getServiceCategories(selectedServiceContract.solutionId, type)
        );
      }

      // eslint-disable-next-line no-console
      console.warn('Beware! This should not happen');
      // eslint-disable-next-line no-console
      console.warn(
        `Each service is either ${ContractBuildType.ITSM} or ${ContractBuildType.CSM}. It cannot be both or nothing`
      );
      return [];
    },
    [SupportSystemService]
  );

  const [entitlements, setEntitlements] = useState<ContractEntitlement[]>([]);

  const { refetch: refetchEntitlements } = useQuery(
    ['service-entitlements', selectedContract],
    async () => {
      const contractEntitlements = await SupportSystemService.listEntitlements(
        selectedContract.contract
      );

      setEntitlements(contractEntitlements);
      return contractEntitlements;
    },
    {
      enabled: false,
    }
  );

  const serviceEntitlementsOptions = useCallback(() => {
    if (!entitlements) {
      return [];
    }

    return entitlements.map((entitlement) => ({
      id: entitlement.id,
      [controlIdsFields.entitlement]: entitlement.name,
    }));
  }, [entitlements]);

  const serviceContractsOptions = useCallback(() => {
    if (!selectedService) {
      return [];
    }

    return selectedService.subContracts.map((subContract) => ({
      id: capitalise(subContract.ccoNumber),
      [controlIdsFields.contract]: subContract.sncNumber,
    }));
  }, [selectedService]);

  const whichCategoryType = useCallback((requestType: string, serviceType: ContractBuildType) => {
    if (serviceType === ContractBuildType.ITSM) {
      return requestType ===
        paramCase(subcategoriesFields.serviceRequest, {
          delimiter: ' ',
        })
        ? subcategoriesFields.srCategories
        : subcategoriesFields.incidentCategories;
    }

    if (serviceType === ContractBuildType.CSM) {
      return requestType === CSMType.Billing
        ? subcategoriesFields.billingCategories
        : paramCase(subcategoriesFields.serviceRequest, {
            delimiter: ' ',
          });
    }

    return '';
  }, []);

  const getCategories = useCallback(async () => {
    if (!selectedService) {
      return [];
    }

    const selectedServiceType = selectedService.buildType;

    const promises = batchCategoriesRequest(selectedService, selectedServiceType);
    const results = await Promise.allSettled(promises);
    const fulfilledResults = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => (result as PromiseFulfilledResult<SubCategory>).value);

    return fulfilledResults.reduce((acc, current) => {
      const [serviceName, requestType] = Object.keys(current)[0].split('@');
      const categoryType = whichCategoryType(requestType, selectedServiceType);

      if (!categoryType) {
        // eslint-disable-next-line no-console
        console.warn('Beware! Something is not configured the correct way');
        // eslint-disable-next-line no-console
        console.warn('requestType/categoryType is expected to exist in all situations.');
        return acc;
      }
      acc[serviceName] = {
        ...acc[serviceName],
        [categoryType]: Object.values(current)[0],
      };
      return acc;
    }, [] as SubCategory[]);
  }, [selectedService, whichCategoryType, batchCategoriesRequest]);

  const { data: resolvedCategories, refetch: refetchCategories } = useQuery(
    ['service-categories', getCategories],
    async () => getCategories(),
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: false,
      structuralSharing: false,
    }
  );

  const getCategoriesWithSubCategories = useCallback(
    (serviceName: string, type: IssueType & CSMType) => {
      if (!resolvedCategories) {
        return {};
      }

      const serviceCategories: SubCategory | undefined = resolvedCategories[serviceName];
      if (!serviceCategories) {
        return {};
      }

      switch (type) {
        case CSMType.Billing:
          return serviceCategories.billingCategories || {};
        case CSMType.Case:
          return (
            serviceCategories[paramCase(subcategoriesFields.serviceRequest, { delimiter: ' ' })] ||
            {}
          );
        case IssueType.Request:
          return serviceCategories.srCategories || {};
        case IssueType.Incident:
          return serviceCategories.incidentCategories || {};
        default:
          return {};
      }
    },
    [resolvedCategories]
  );

  const cmdbActiveServiceNames = useMemo(
    () => new Set(serviceOptions.filter((item) => item.cmdbCI).map((item) => item.serviceName)),
    [serviceOptions]
  );

  const serviceCategoriesOptions = useCallback(
    (serviceName: string, type: IssueType & CSMType) => {
      const categoriesPackage = getCategoriesWithSubCategories(serviceName, type);

      const categoriesToDisplay = Object.keys(categoriesPackage)
        .filter(Boolean)
        .map((category) => ({
          id: capitalise(category),
          [controlIdsFields.category]: category,
        }));

      return categoriesToDisplay;
    },
    [getCategoriesWithSubCategories]
  );

  const serviceSubCategoriesOptions = useCallback(
    (serviceName: string, type: IssueType & CSMType, selectedCategory: string) => {
      const categoriesPackage = getCategoriesWithSubCategories(serviceName, type);

      const { subCategories = [] } = categoriesPackage[selectedCategory] || {};
      const subCategoriesToDisplay = subCategories.filter(Boolean).map((subCategory: string) => ({
        id: capitalise(subCategory),
        [controlIdsFields.subCategory]: subCategory,
      }));
      return subCategoriesToDisplay;
    },
    [getCategoriesWithSubCategories]
  );

  const serviceSubCategoryTasksOptions = useCallback(
    (
      serviceName: string,
      type: IssueType & CSMType,
      selectedCategory: string,
      selectedSubCategory: string
    ) => {
      const categoriesPackage = getCategoriesWithSubCategories(serviceName, type);

      const { subCategoryTasks = [] } = categoriesPackage[selectedCategory] || {};
      const subCategoryTasksToDisplay = subCategoryTasks[selectedSubCategory]
        .filter(Boolean)
        .map((subCategoryTask: string) => ({
          id: capitalise(subCategoryTask),
          [controlIdsFields.subCategoryTask]: subCategoryTask,
        }));
      return subCategoryTasksToDisplay;
    },
    [getCategoriesWithSubCategories]
  );

  const serviceImpactOptions = useCallback((serviceType: IssueType & CSMType) => {
    if (serviceType === IssueType.Incident) {
      const enumValues = Object.keys(IncidentImpactEnum);
      const impactsToDisplay = enumValues
        .slice(enumValues.length / 2)
        .filter((key) => !['Emergency', 'Critical'].includes(key))
        .map((key) => ({
          id: capitalise(key),
          [controlIdsFields.impact]: IncidentImpactEnum[key],
        }));
      return impactsToDisplay;
    }

    if (serviceType === IssueType.Request) {
      const enumValues = Object.keys(RequestImpactEnum);
      const impactsToDisplay = enumValues.slice(enumValues.length / 2).map((key) => ({
        id: capitalise(key),
        [controlIdsFields.impact]: RequestImpactEnum[key],
      }));
      return impactsToDisplay;
    }

    if (serviceType === CSMType.Case) {
      const enumValues = Object.keys(CaseImpactOrder);
      const impactsToDisplay = enumValues.map((key) => ({
        id: capitalise(key),
        [controlIdsFields.impact]: CaseImpactOrder[key],
      }));
      return impactsToDisplay;
    }

    return [];
  }, []);

  const mapToServerBillingCreateRequest = async (
    values: Omit<BillingCreateFormValues, 'type'>
  ): Promise<ServiceNowCreateBillingRequest> => {
    const {
      filesAttached = [],
      serviceName,
      shortDescription,
      description,
      cmdbConfigItem,
    } = values;

    const filesAttachedBase64 = (filesAttached as UploadFileInfo[]).map(async (file) => ({
      filename: file.name,
      content: await fileToBase64(file.getRawFile()),
    }));
    const awaitedFilesAttachedBase64Ready = await Promise.all(filesAttachedBase64);

    return {
      filesAttached: awaitedFilesAttachedBase64Ready,
      serviceName: serviceName.serviceName,
      shortDescription,
      description,
      cmdbConfigItem: cmdbConfigItem?.id,
    };
  };

  const mapToServerCaseBillingCreateRequest = async (
    values: Omit<BillingCreateFormValues, 'type'>
  ): Promise<ServiceNowCreateCaseRequest> => {
    const {
      filesAttached = [],
      serviceName,
      shortDescription,
      description,
      cmdbConfigItem,
    } = values;

    const filesAttachedBase64 = (filesAttached as UploadFileInfo[]).map(async (file) => ({
      filename: file.name,
      content: await fileToBase64(file.getRawFile()),
    }));
    const awaitedFilesAttachedBase64Ready = await Promise.all(filesAttachedBase64);

    return {
      title: shortDescription,
      attachments: awaitedFilesAttachedBase64Ready,
      product: serviceName?.serviceName,
      description,
      // @ts-ignore
      cmdbConfigItem: cmdbConfigItem?.id,
      solutionId: serviceName?.solutionId,
      impact: SnowCaseImpact.Low,
    };
  };

  const mapToServerCaseCreateRequest = async (
    values: CaseCreateFormValues
  ): Promise<ServiceNowCreateCaseRequest> => {
    const {
      filesAttached = [],
      shortDescription,
      description,
      category,
      serviceName,
      subCategory,
      impact,
      contract,
      entitlement,
      cmdbConfigItem,
    } = values;

    const filesAttachedBase64 = (filesAttached as UploadFileInfo[]).map(async (file) => ({
      filename: file.name,
      content: await fileToBase64(file.getRawFile()),
    }));
    const awaitedFilesAttachedBase64Ready = await Promise.all(filesAttachedBase64);

    return {
      title: shortDescription,
      attachments: awaitedFilesAttachedBase64Ready,
      description: description || '',
      product: serviceName?.serviceName,
      solutionId: serviceName?.solutionId,
      category: category?.category || '',
      subCategory: subCategory?.subCategory || '',
      impact: impact?.id as SnowCaseImpact | undefined,
      contractId: contract?.contract ?? '',
      entitlementId: entitlement?.id,
      cmdbConfigItem: cmdbConfigItem?.id,
    };
  };

  const mapToServerIncidentAndRequestCreateRequest = async (
    values: TicketCreateFormValues
  ): Promise<ServiceNowCreateRequest | ServiceNowCreateIncidentRequest> => {
    const {
      filesAttached = [],
      serviceName,
      category,
      subCategory,
      subCategoryTask,
      shortDescription,
      description,
      impact,
      cmdbConfigItem,
    } = values;

    const filesAttachedBase64 = (filesAttached as UploadFileInfo[]).map(async (file) => ({
      filename: file.name,
      content: await fileToBase64(file.getRawFile()),
    }));
    const awaitedFilesAttachedBase64Ready = await Promise.all(filesAttachedBase64);

    return {
      filesAttached: awaitedFilesAttachedBase64Ready,
      serviceName: serviceName?.serviceName,
      category: category?.category,
      subCategory: subCategory?.subCategory,
      subCategoryTask: subCategoryTask?.subCategoryTask,
      impact: impact?.id as SnowRequestImpact | undefined,
      shortDescription,
      description,
      cmdbConfigItem: cmdbConfigItem?.id,
    };
  };

  const queryClient = useQueryClient();

  const createCase = async (values: ControlFieldIdsCSM & ControlFieldIdsITSM): Promise<void> => {
    const { type } = values;
    const isITSM = values?.serviceName?.buildType === ContractBuildType.ITSM;

    try {
      if (type === IssueType.Billing || type === CSMType.Billing) {
        if (isITSM) {
          const request = await mapToServerBillingCreateRequest(values as BillingCreateFormValues);
          await SupportSystemService.createBilling(request);

          toast.success(translateText(LocaleTranslationKeysEnum.SupportFormSuccessSend));
        } else {
          const request = await mapToServerCaseBillingCreateRequest(
            values as BillingCreateFormValues
          );
          const caseResponse = await SupportSystemService.createCase(request);

          toast.success(
            createElement(
              'div',
              {
                style: { display: 'flex', gap: '0.5rem' },
              },
              [
                translateText(LocaleTranslationKeysEnum.SupportFormCaseSuccessSend),
                createElement(
                  'a',
                  {
                    href: RoutePathsEnum.CaseDetail.replace(
                      RoutePathSuffixEnum.RecordId,
                      caseResponse.caseNumber
                    ),
                    target: '_blank',
                  },
                  translateText(LocaleTranslationKeysEnum.SupportFormCaseSuccessSendDetails)
                ),
              ]
            )
          );
        }
      }

      if (type === IssueType.Request) {
        const request = (await mapToServerIncidentAndRequestCreateRequest(
          values
        )) as ServiceNowCreateRequest;
        await SupportSystemService.createRequest(request);

        toast.success(translateText(LocaleTranslationKeysEnum.SupportFormSuccessSend));
      }

      if (type === IssueType.Incident) {
        const request = (await mapToServerIncidentAndRequestCreateRequest(
          values
        )) as ServiceNowCreateIncidentRequest;
        await SupportSystemService.createIncident(request);

        toast.success(translateText(LocaleTranslationKeysEnum.SupportFormSuccessSend));
      }

      if (type === CSMType.Case) {
        const request = await mapToServerCaseCreateRequest(values as CaseCreateFormValues);
        const caseResponse = await SupportSystemService.createCase(request);

        toast.success(
          createElement(
            'div',
            {
              style: { display: 'flex', gap: '0.5rem' },
            },
            [
              translateText(LocaleTranslationKeysEnum.SupportFormCaseSuccessSend),
              createElement(
                'a',
                {
                  href: RoutePathsEnum.CaseDetail.replace(
                    RoutePathSuffixEnum.RecordId,
                    caseResponse.caseNumber
                  ),
                  target: '_blank',
                },
                translateText(LocaleTranslationKeysEnum.SupportFormCaseSuccessSendDetails)
              ),
            ]
          )
        );
      }

      if (isITSM) {
        queryClient.refetchQueries(['support-system-tickets', scope]);
        if (scope === 'individual') {
          return navigate(RoutePathsEnum.SupportSystemTicketsIndividual);
        }
        return navigate(RoutePathsEnum.SupportSystemTicketsCompany);
      }
      queryClient.refetchQueries(['cases', scope]);
      if (scope === 'individual') {
        return navigate(RoutePathsEnum.CasesIndividual);
      }
      return navigate(RoutePathsEnum.CasesCompany);
    } catch (error) {
      toast.error(translateText(LocaleTranslationKeysEnum.SupportFormErrorSend));
      // eslint-disable-next-line no-console
      console.error(error);
      return undefined;
    }
  };

  useEffect(() => {
    if (!selectedContract || !selectedService || !areEntitlementsEnabled) {
      return;
    }
    refetchEntitlements()
      .then(() => {
        deferredContractsPromiseRef.current.resolve();
      })
      .catch(() => {
        deferredContractsPromiseRef.current.reject();
      });
  }, [selectedContract, selectedService, refetchEntitlements, areEntitlementsEnabled]);

  useEffect(() => {
    if (selectedService) {
      refetchCategories()
        .then(() => {
          if (!selectedService.cmdbCI) {
            deferredPromiseRef.current?.resolve();
            deferredPromiseRef.current = null;
            return;
          }
          refetchConfigurationItems()
            .then(() => {
              deferredPromiseRef.current?.resolve();
              deferredPromiseRef.current = null;
            })
            .catch(() => {
              deferredPromiseRef.current?.reject();
              deferredPromiseRef.current = null;
            });
        })
        .catch(() => {
          deferredPromiseRef.current?.reject();
          deferredPromiseRef.current = null;
        });
    }
  }, [selectedService, refetchCategories, deferredPromiseRef, refetchConfigurationItems]);

  const refetchServiceCategories = useCallback(
    async (service: Contract) => {
      const deferredPromise = createDeferredPromise<void>();
      deferredPromiseRef.current = deferredPromise;
      if (service) {
        setSelectedService({ ...service });
      }
      return deferredPromise.promise;
    },
    [deferredPromiseRef, setSelectedService]
  );

  const refetchServiceContracts = useCallback(
    async (contract: { id: string; contract: string }) => {
      setEntitlements([]);
      const deferredContractsPromise = createDeferredPromise<void>();
      deferredContractsPromiseRef.current = deferredContractsPromise;

      if (contract) {
        setSelectedContract({ ...contract });
      }

      return deferredContractsPromise.promise;
    },
    [setSelectedContract]
  );

  return {
    refetchServiceCategories,
    refetchServiceContracts,
    createCase,
    serviceOptions: serviceOptions.map((service) => ({
      id: capitalise(service.serviceName),
      ...service,
    })),
    cmdbActiveServiceNames,
    serviceCategoriesOptions,
    serviceEntitlementsOptions,
    serviceContractsOptions,
    serviceImpactOptions,
    configurationItems,
    serviceSubCategoriesOptions,
    serviceSubCategoryTasksOptions,
  };
};
